import { useEffect, useMemo, useState } from "react";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import { RichTextEditor } from "../../components/RichTextEditor";
import { AttendeesField } from "./AttendeesField";
import type { MeetingNote, MeetingNoteKind } from "../../api/types";

// 一份會議紀錄的欄位(第 18 輪,2026-09-17 抽出來共用)。
//
// ==========================================================================
// 只有欄位,沒有外框
// ==========================================================================
// 外框由呼叫端決定 —— 現在是一整頁(`MeetingNotePage`)。抽出來是因為
// 業主 2026-09-17 說「新增不要用懸浮視窗 因為可能會議可能會很長」,
// 而把整份表單搬進頁面的時候,**存檔那一段邏輯不可以跟著複製一份**:
// 複製了就會有兩套規矩,而「標籤不送、由後端補」這種事只會有一邊記得。
//
// ==========================================================================
// 三種紀錄共用這一份
// ==========================================================================
// 會議紀錄 / 結案報告 / 檢討報告的欄位一模一樣,差別只在最上面那個下拉。
//
// **開會日不是建立日** —— 補寫上週的紀錄是常態,而用建立日會讓那份紀錄
// 排在列表上錯的位置。
//
// 不用 react-query:這一份會被報價單編輯器那棵樹用到,而那棵樹的測試
// 沒有 `QueryClientProvider`(與 `AttachmentPanel` / `ProjectCost` 一致)。

const KINDS: [MeetingNoteKind, string][] = [
  ["MEETING", "會議紀錄"],
  ["CLOSING", "結案報告"],
  ["REVIEW", "檢討報告"],
];

export interface MeetingNoteFormProps {
  /** 有值就是改,沒有就是新增。 */
  note?: MeetingNote;
  /** 從報價單上開的就帶著它。 */
  quoteId?: number | null;
  /** 內容框幾行高。整頁給得多,擠在視窗裡只能給少。 */
  rows?: number;
  onDone: () => void;
  /** 呼叫端自己畫按鈕 —— 頁面放在上面,對話框放在頁尾。 */
  children?: (state: {
    save: () => void;
    saving: boolean;
    /** 標題與日期是唯二非有不可的。 */
    ready: boolean;
    editing: boolean;
  }) => React.ReactNode;
}

export function MeetingNoteForm({
  note,
  quoteId,
  rows = 10,
  onDone,
  children,
}: MeetingNoteFormProps) {
  const toast = useToast();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [kind, setKind] = useState<MeetingNoteKind>(note?.kind ?? "MEETING");
  const [title, setTitle] = useState(note?.title ?? "");
  const [metOn, setMetOn] = useState(note?.met_on ?? today);
  const [attendees, setAttendees] = useState(note?.attendees ?? "");
  const [body, setBody] = useState(note?.body ?? "");
  const [saving, setSaving] = useState(false);

  /** 把一張圖丟給後端,拿回一個網址插進內文。
   *
   * **還沒存過的那一篇也貼得了圖。** 第一版要求先按儲存(業主 2026-09-19:
   * 「錯誤:先按一次儲存,再插入圖片」)—— 而那道關卡沒有任何作用:
   * 後端路徑上的紀錄編號查出來之後一次都沒被用到,所以整個拿掉了。
   */
  const uploadImage = async (file: File): Promise<string | null> => {
    const form = new FormData();
    form.append("file", file);
    try {
      const got = await api.upload<{ url: string }>("/meeting-notes/images", form);
      return got.url;
    } catch {
      toast.push("圖片上傳失敗", "error");
      return null;
    }
  };

  useEffect(() => {
    if (!note) return;
    setKind(note.kind);
    setTitle(note.title);
    setMetOn(note.met_on);
    setAttendees(note.attendees);
    setBody(note.body ?? "");
  }, [note]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        kind,
        quote_id: note ? note.quote_id : (quoteId ?? null),
        title,
        met_on: metOn,
        attendees,
        body,
        // **連結不送了**(第 22 輪)。提及在內文裡,後端從 `body` 推導 ——
        // 送第二份的話兩邊會分岔,而那正是業主說「做得很差」的那個舊做法。
        links: [],
      };
      if (note) {
        await api.put<MeetingNote>(`/meeting-notes/${note.id}`, payload);
      } else {
        await api.post<MeetingNote>("/meeting-notes", payload);
      }
      toast.push(note ? "已更新" : "已建立", "success");
      onDone();
    } catch (err) {
      toast.push(err instanceof Error ? err.message : "存不起來", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {children?.({
        save: () => void save(),
        saving,
        ready: Boolean(title.trim() && metOn),
        editing: Boolean(note),
      })}

      <Select
        label="種類"
        value={kind}
        onChange={(e) => setKind(e.target.value as MeetingNoteKind)}
        options={KINDS.map(([value, text]) => ({ value, label: text }))}
      />

      <Input label="標題" value={title} onChange={(e) => setTitle(e.target.value)} />
      <Input
        label="開會日"
        type="date"
        value={metOn}
        onChange={(e) => setMetOn(e.target.value)}
      />
      <AttendeesField value={attendees} onChange={setAttendees} />

      {/* 業主 2026-09-18:「會議記錄系統撰寫要跟 office WORD 一樣」。
          消毒在後端(`app/sanitize.py`)—— 這裡產的 HTML 一律洗過才進資料庫。 */}
      <RichTextEditor
        value={body}
        onChange={setBody}
        onUploadImage={uploadImage}
        minHeight={`${rows * 1.6}rem`}
      />

    </>
  );
}
